import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UsersThree, MapPin, Package, ClockClockwise } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { memoryApi } from "@/api";

export default function Memory() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [places, setPlaces] = useState([]);
  const [objects, setObjects] = useState([]);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    const load = () => {
      memoryApi.characters(pid).then(setCharacters).catch(() => {});
      memoryApi.places(pid).then(setPlaces).catch(() => {});
      memoryApi.objects(pid).then(setObjects).catch(() => {});
      memoryApi.timeline(pid).then(setTimeline).catch(() => {});
    };
    load();
    const iv = setInterval(load, 6000); // remplissage progressif pendant l'analyse
    return () => clearInterval(iv);
  }, [pid]);

  const goChapter = (idx) => {
    // navigate to editor; chapter index -> find by list in editor
    navigate(`/project/${pid}/editor`);
  };

  return (
    <div className="p-8 max-w-5xl fade-up">
      <p className="overline text-primary mb-1">Mémoire du roman</p>
      <h1 className="font-serif text-4xl font-light tracking-tight mb-6">Intelligence narrative</h1>

      <Tabs defaultValue="characters">
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 mb-6">
          {[
            { v: "characters", l: "Personnages", i: UsersThree, n: characters.length },
            { v: "places", l: "Lieux", i: MapPin, n: places.length },
            { v: "objects", l: "Objets", i: Package, n: objects.length },
            { v: "timeline", l: "Chronologie", i: ClockClockwise, n: timeline.length },
          ].map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`memory-tab-${t.v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none bg-transparent gap-2 pb-3">
              <t.i size={17} weight="duotone" /> {t.l} <span className="text-xs text-muted-foreground">{t.n}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="characters">
          {characters.length === 0 ? <Empty label="Aucun personnage pour l'instant. La mémoire se remplit automatiquement au fil de l'analyse, chapitre après chapitre." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {characters.map((c) => (
                <div key={c.id} data-testid={`character-${c.name}`} className="border border-border bg-surface rounded-lg p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-serif text-2xl">{c.name}</h3>
                    <Badge variant="secondary" className="font-mono">{c.appearances} chap.</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Première apparition : chapitre {(c.first_chapter ?? 0) + 1}</p>
                  {Object.keys(c.attributes || {}).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {Object.entries(c.attributes).map(([k, v]) => (
                        <span key={k} className="text-xs px-2 py-1 rounded-sm bg-secondary text-secondary-foreground">
                          <span className="text-muted-foreground">{k}:</span> {Array.isArray(v) ? v.join(", ") : String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {c.chapters.slice(0, 24).map((ch) => (
                      <button key={ch} onClick={() => goChapter(ch)} className="text-xs font-mono w-7 h-7 grid place-items-center rounded-sm border border-border hover:border-primary hover:text-primary transition-colors">{ch + 1}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="places">
          {places.length === 0 ? <Empty label="Aucun lieu extrait." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {places.map((p) => (
                <div key={p.id} data-testid={`place-${p.name}`} className="border border-border bg-surface rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2"><MapPin size={18} weight="duotone" className="text-primary" /><h3 className="font-serif text-xl">{p.name}</h3></div>
                  {p.description && <p className="text-sm text-muted-foreground mb-3">{p.description}</p>}
                  <p className="text-xs text-muted-foreground font-mono">Chapitres : {p.chapters.map((c) => c + 1).join(", ")}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="objects">
          {objects.length === 0 ? <Empty label="Aucun objet important détecté." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {objects.map((o) => (
                <div key={o.id} data-testid={`object-${o.name}`} className="border border-border bg-surface rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2"><Package size={18} weight="duotone" className="text-primary" /><h3 className="font-serif text-xl">{o.name}</h3></div>
                  {o.note && <p className="text-sm text-muted-foreground mb-2">{o.note}</p>}
                  <p className="text-xs text-muted-foreground font-mono">Première : ch. {(o.first_chapter ?? 0) + 1} · Dernière : ch. {(o.last_chapter ?? 0) + 1}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline">
          {timeline.length === 0 ? <Empty label="Aucun élément temporel extrait." /> : (
            <div className="relative border-l-2 border-border ml-3 pl-6 space-y-5">
              {timeline.map((e) => (
                <div key={e.id} data-testid={`timeline-${e.id}`} className="relative">
                  <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full bg-primary border-2 border-background" />
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs px-2 py-0.5 rounded-sm bg-secondary">Ch. {e.chapter_index + 1}</span>
                    {e.time_reference && <span className="text-sm font-medium text-primary">{e.time_reference}</span>}
                    {e.relative && <span className="text-xs text-muted-foreground italic">({e.relative})</span>}
                  </div>
                  <p className="text-sm text-foreground">{e.event}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ label }) {
  return <div className="border border-dashed border-border rounded-lg py-16 text-center text-muted-foreground">{label}</div>;
}
